import React, { useState, useEffect, memo, useRef } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Chip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Switch,
  useTheme,
  alpha
} from '@mui/material';
import { PamGrid } from '@1823-partners/core';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import AdcoGraphModal from './AdcoGraphModal';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

const AdcoPanel = memo(({
  dealName,
  assetType,
  tranches,
  selectedTranche,
  onTrancheChange,
  modifiedDate,
  adcoData,
  adcoEnabled,
  onRunAdco,
  loading
}) => {
  // Set default value for loading
  const isLoading = loading ?? false;
  const theme = useTheme();
  const gridRef = useRef(null);

  const [columnDefs, setColumnDefs] = useState([]);
  const [condensedView, setCondensedView] = useState(true);
  const [graphModalOpen, setGraphModalOpen] = useState(false);

  // Filter ADCO data by selected tranche
  const filteredAdcoData = React.useMemo(() => {
    if (!adcoData?.length) {
      return [];
    }

    // If no tranche selected, show all data
    if (!selectedTranche) {
      return adcoData;
    }

    // Filter data to only show rows for the selected tranche
    return adcoData.filter(row => {
      const trancheValue = row.TRANCHE || row.Tranche || row.tranche;
      return trancheValue === selectedTranche;
    });
  }, [adcoData, selectedTranche]);

  // Transform data for condensed view
  const condensedData = React.useMemo(() => {
    if (!filteredAdcoData?.length) {
      return [];
    }

    // Define the metrics we want to condense
    const metrics = ['CRR', 'CDR', 'SEV', 'DLNQ60'];

    const result = metrics.map(metric => {
      // Collect all values for this metric from filtered data
      const values = filteredAdcoData
        .map(row => {
          const val = row[metric];
          if (val == null) return '';

          // Format dates as YYYY-MM-DD
          if (metric === 'DATE' || metric === 'ASOFDT') {
            const parsed = dayjs(val);
            return parsed.isValid() ? parsed.format('YYYY-MM-DD') : val;
          }

          // Format numeric values
          return Number(val).toFixed(2);
        })
        .filter(v => v !== '');

      return {
        metric: metric,
        values: values.join('  ')  // Double space for better readability
      };
    });

    return result.filter(item => item.values); // Only include metrics with values
  }, [filteredAdcoData]);

  // Define column definitions based on view mode
  useEffect(() => {
    if (condensedView) {
      // Condensed view columns
      const cols = [
        {
          field: 'metric',
          headerName: 'Metric',
          width: 120,
          pinned: 'left',
          cellStyle: {
            fontWeight: 600,
            fontSize: '13px'
          }
        },
        {
          field: 'values',
          headerName: 'Values',
          flex: 1,
          cellStyle: {
            fontSize: '13px',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }
        },
      ];
      setColumnDefs(cols);
    } else {
      // Standard view columns
      const cols = [
        {
          field: 'DATE',
          headerName: 'Date',
          flex: 1,
          minWidth: 120,
          filter: 'agDateColumnFilter',
          filterParams: {
            comparator: (filterDate, cellValue) => {
              if (!cellValue) return -1;
              const cellDate = dayjs(cellValue);
              if (!cellDate.isValid()) return -1;
              const filterDay = dayjs(filterDate).startOf('day');
              const cellDay = cellDate.startOf('day');
              if (cellDay.isBefore(filterDay)) return -1;
              if (cellDay.isAfter(filterDay)) return 1;
              return 0;
            }
          },
          valueFormatter: params => {
            if (!params.value) return '';
            // Date-only field: render in UTC frame to avoid a day-shift west of UTC.
            const parsed = dayjs.utc(params.value);
            return parsed.isValid() ? parsed.format('YYYY-MM-DD') : params.value;
          }
        },
        {
          field: 'CRR',
          headerName: 'CRR',
          flex: 1,
          minWidth: 90,
          valueFormatter: params => params.value != null ? Number(params.value).toFixed(4) : ''
        },
        {
          field: 'CDR',
          headerName: 'CDR',
          flex: 1,
          minWidth: 90,
          valueFormatter: params => params.value != null ? Number(params.value).toFixed(4) : ''
        },
        {
          field: 'SEV',
          headerName: 'SEV',
          flex: 1,
          minWidth: 90,
          valueFormatter: params => params.value != null ? Number(params.value).toFixed(4) : ''
        },
        {
          field: 'DLNQ60',
          headerName: 'DLNQ',
          flex: 1,
          minWidth: 90,
          valueFormatter: params => params.value != null ? Number(params.value).toFixed(4) : ''
        },
        { field: 'CUSIP', headerName: 'CUSIP', flex: 1.2, minWidth: 120 },
        { field: 'TRANCHE', headerName: 'Tranche', flex: 1, minWidth: 100 },
        {
          field: 'ASOFDT',
          headerName: 'ASOFDT',
          flex: 1,
          minWidth: 120,
          filter: 'agDateColumnFilter',
          filterParams: {
            comparator: (filterDate, cellValue) => {
              if (!cellValue) return -1;
              const cellDate = dayjs(cellValue);
              if (!cellDate.isValid()) return -1;
              const filterDay = dayjs(filterDate).startOf('day');
              const cellDay = cellDate.startOf('day');
              if (cellDay.isBefore(filterDay)) return -1;
              if (cellDay.isAfter(filterDay)) return 1;
              return 0;
            }
          },
          valueFormatter: params => {
            if (!params.value) return '';
            // Date-only field: render in UTC frame to avoid a day-shift west of UTC.
            const parsed = dayjs.utc(params.value);
            return parsed.isValid() ? parsed.format('YYYY-MM-DD') : params.value;
          }
        },
      ];
      setColumnDefs(cols);
    }
  }, [condensedView]);

  // Handle export via PamGrid ref
  const handleExport = () => {
    if (gridRef.current?.api) {
      gridRef.current.api.exportDataAsExcel({
        fileName: `adco${selectedTranche ? `_${selectedTranche}` : ''}.xlsx`,
        sheetName: 'ADCO',
      });
    }
  };

  // Show enabled tranches
  const getEnabledTranches = () => {
    if (!adcoEnabled || typeof adcoEnabled !== 'object') return [];
    return Object.entries(adcoEnabled)
      .filter(([_, enabled]) => enabled)
      .map(([tranche, _]) => tranche);
  };

  const enabledTranches = getEnabledTranches();

  if (!dealName) {
    return null;
  }

  return (
    <Card
      elevation={4}
      sx={{
        borderRadius: 3,
        background: theme.palette.mode === 'dark'
          ? `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${alpha(theme.palette.background.paper, 0.8)} 100%)`
          : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
        border: `1px solid ${theme.palette.divider}`,
        boxShadow: theme.palette.mode === 'dark'
          ? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
          : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          px: 3,
          py: 2.5,
          bgcolor: theme.palette.mode === 'dark' ? '#1e293b' : '#1e293b',
          color: 'white',
          borderBottom: theme.palette.mode === 'dark' ? '1px solid #334155' : '1px solid #334155'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                fontSize: '16px',
                mb: 0.5,
                letterSpacing: '-0.025em'
              }}
            >
              ADCO Data
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.7)',
                fontSize: '12px'
              }}
            >
              {enabledTranches.length > 0
                ? `Enabled for: ${enabledTranches.join(', ')}`
                : 'No tranches enabled for ADCO'
              }
            </Typography>
          </Box>
          {isLoading && (
            <CircularProgress size={16} sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
          )}
        </Box>
      </Box>
      <CardContent sx={{ p: 3, bgcolor: theme.palette.background.paper }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
            {/* Tranche Dropdown */}
            {tranches.length > 0 && (
              <FormControl sx={{ minWidth: 150 }}>
                <InputLabel sx={{ fontSize: '14px' }}>Tranche</InputLabel>
                <Select
                  value={selectedTranche || ''}
                  onChange={(e) => onTrancheChange(e.target.value)}
                  label="Tranche"
                  sx={{
                    height: 40,
                    fontSize: '14px',
                    bgcolor: theme.palette.background.paper,
                    '& .MuiSelect-select': {
                      py: 1
                    }
                  }}
                >
                  {tranches.map(t => (
                    <MenuItem key={t} value={t} sx={{ fontSize: '14px' }}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Condensed View Toggle */}
            <FormControlLabel
              control={
                <Switch
                  checked={condensedView}
                  onChange={(e) => setCondensedView(e.target.checked)}
                  color="primary"
                />
              }
              label={
                <Typography sx={{ fontSize: '14px', fontWeight: 500 }}>
                  Condensed View
                </Typography>
              }
            />
          </Box>

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<ShowChartIcon />}
              onClick={() => setGraphModalOpen(true)}
              disabled={!adcoData || adcoData.length === 0}
              sx={{
                fontWeight: 600,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontSize: '14px',
                borderRadius: 2,
                transition: 'all 0.2s ease'
              }}
            >
              View Chart
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={!filteredAdcoData || filteredAdcoData.length === 0}
              onClick={handleExport}
              sx={{
                fontWeight: 600,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontSize: '14px',
                borderRadius: 2,
                transition: 'all 0.2s ease'
              }}
            >
              Export
            </Button>

            <Button
              variant="contained"
              color="success"
              startIcon={isLoading ? <CircularProgress size={16} sx={{ color: 'white' }} /> : <PlayArrowIcon />}
              onClick={onRunAdco}
              disabled={isLoading || enabledTranches.length === 0}
              sx={{
                fontWeight: 600,
                px: 3,
                py: 1,
                textTransform: 'none',
                fontSize: '14px',
                borderRadius: 2,
                '&:hover:not(:disabled)': {
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease'
              }}
            >
              {isLoading ? 'Running...' : 'Run ADCO'}
            </Button>
          </Box>
        </Box>

      {enabledTranches.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {tranches.map(tranche => (
              <Chip
                key={tranche}
                label={tranche}
                size="small"
                sx={{
                  bgcolor: enabledTranches.includes(tranche) ? '#d1fae5' : '#f3f4f6',
                  color: enabledTranches.includes(tranche) ? '#065f46' : '#6b7280',
                  fontWeight: 600,
                  fontSize: '12px'
                }}
              />
            ))}
          </Box>
        </Box>
      )}

      {(condensedView ? condensedData.length > 0 : filteredAdcoData.length > 0) ? (
        <Box
          sx={{
            height: condensedView ? 200 : 400,
            width: '100%',
            position: 'relative',
            '& .ag-row': condensedView ? {
              height: '36px !important',
              minHeight: '36px !important'
            } : {},
            '& .ag-cell': condensedView ? {
              lineHeight: '36px !important',
              paddingTop: '0 !important',
              paddingBottom: '0 !important'
            } : {}
          }}
        >
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          )}
          <PamGrid
            ref={gridRef}
            columnDefs={columnDefs}
            rowData={condensedView ? condensedData : filteredAdcoData}
            defaultColDef={{
              sortable: true,
              filter: true,
              resizable: true,
              editable: false,
            }}
            height="100%"
            containerSx={{ height: '100%' }}
            gridContainerSx={{ height: '100%' }}
            showQuickFilter={false}
            showFooter={false}
            showToolbar={false}
            showExport={false}
            rowHeight={condensedView ? 36 : undefined}
            suppressRowHoverHighlight={false}
            suppressCellFocus={false}
            animateRows={false}
            suppressColumnVirtualisation={false}
            suppressRowVirtualisation={false}
          />
        </Box>
      ) : (
        <Box
          sx={{
            p: 4,
            textAlign: 'center',
            color: theme.palette.text.secondary,
            border: `2px dashed ${theme.palette.divider}`,
            borderRadius: 2,
            bgcolor: theme.palette.mode === 'dark' ? alpha(theme.palette.background.default, 0.3) : '#fafafa',
            position: 'relative',
            minHeight: 100
          }}
        >
          {isLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: alpha(theme.palette.background.paper, 0.8),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                borderRadius: 2
              }}
            >
              <CircularProgress color="primary" />
            </Box>
          )}
          <Typography variant="body2">
            {enabledTranches.length === 0
              ? 'ADCO is not enabled for any tranches in this deal'
              : 'No ADCO data available. Click "Run ADCO" to generate.'
            }
          </Typography>
        </Box>
      )}
      </CardContent>

      {/* Graph Modal */}
      <AdcoGraphModal
        open={graphModalOpen}
        onClose={() => setGraphModalOpen(false)}
        adcoData={adcoData}
        tranches={tranches}
      />
    </Card>
  );
});

AdcoPanel.displayName = 'AdcoPanel';

export default AdcoPanel;
